import React, { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { throttle } from 'lodash';
import type Highcharts from 'highcharts';

type Chart = Highcharts.Chart;
type Point = Highcharts.Point;
type RegisterChart = (key: string) => (chart: Chart | null) => void;

interface UseChartSyncOptions {
  enabled?: boolean;
  syncTooltips?: boolean;
  syncPosition?: boolean;
}

interface ListenerPair {
  move: EventListener;
  leave: EventListener;
  cancelMove: () => void;
}

interface SyncState {
  charts: Map<string, Chart>;
  listeners: Map<string, ListenerPair>;
  /** Cache of per-key callbacks to ensure stable references across renders */
  callbacks: Map<string, (chart: Chart | null) => void>;
  /** Points that were set to 'hover' by the last syncToX call, so we can clear them efficiently. */
  prevSyncedPoints: Point[];
}

/**
 * Synchronizes crosshair across multiple Highcharts chart instances, with an
 * opt-in mode to also mirror tooltips across sibling charts.
 *
 * Returns a `registerChart` function: call `registerChart(key)` per chart to get
 * an `onChartRef` callback compatible with `UdeDataView`'s `onChartRef` prop.
 *
 * When the user hovers a point in any registered chart, all sibling charts show
 * the crosshair at the same x-value (timestamp). Tooltips stay on the directly
 * hovered chart unless `syncTooltips` is enabled.
 */
export function useChartSync(options: UseChartSyncOptions = {}): (key: string) => (chart: Chart | null) => void {
  const { enabled = true, syncTooltips = false, syncPosition = true } = options;
  const stateRef = useRef<SyncState>({
    charts: new Map(),
    listeners: new Map(),
    callbacks: new Map(),
    prevSyncedPoints: []
  });
  const syncTooltipsRef = useRef(syncTooltips);
  const enabledRef = useRef(enabled);
  const syncPositionRef = useRef(syncPosition);

  syncTooltipsRef.current = syncTooltips;
  enabledRef.current = enabled;
  syncPositionRef.current = syncPosition;

  /**
   * Given a source chart and the x-value of the hovered point,
   * show crosshair and, when enabled, tooltip at the same x on sibling charts.
   */
  const syncToX = useCallback((sourceKey: string, x: number, sourceRelativeY?: number) => {
    if (!enabledRef.current) {
      return;
    }

    const state = stateRef.current;

    // Clear previously synced hover states before setting new ones.
    // This prevents dots from being "left behind" when the mouse moves quickly.
    for (const p of state.prevSyncedPoints) {
      try {
        p.setState();
      } catch {
        // point may have been destroyed after a chart redraw
      }
    }
    const nextSynced: Point[] = [];

    state.charts.forEach((chart, key) => {
      if (key === sourceKey) {
        return;
      }

      const pointsAtX: Point[] = [];
      chart.series.forEach((series) => {
        if (!series.visible) {
          return;
        }
        const point = series.points?.find((p) => p?.x === x);
        if (point) {
          point.setState('hover');
          pointsAtX.push(point);
          nextSynced.push(point);
        }
      });

      if (pointsAtX.length > 0) {
        chart.xAxis?.[0]?.drawCrosshair(undefined, pointsAtX[0]);

        if (syncTooltipsRef.current) {
          if (syncPositionRef.current) {
            const anchor = pointsAtX[0];
            const chartX = (anchor.plotX ?? 0) + chart.plotLeft;
            const chartY = chart.plotTop + (sourceRelativeY ?? 0.5) * chart.plotHeight;
            chart.tooltip?.refresh(chart.tooltip.shared ? pointsAtX : pointsAtX[0], {
              chartX,
              chartY
            } as Highcharts.PointerEventObject);
          } else {
            chart.tooltip?.refresh(chart.tooltip.shared ? pointsAtX : pointsAtX[0]);
          }
        }

        return;
      }

      chart.xAxis?.[0]?.hideCrosshair();
      chart.tooltip?.hide();
    });

    state.prevSyncedPoints = nextSynced;
  }, []);

  /**
   * Clear hover state, tooltip, and crosshair on all charts except the source.
   */
  const clearSync = useCallback((sourceKey: string) => {
    const state = stateRef.current;

    // Clear tracked synced hover states
    for (const p of state.prevSyncedPoints) {
      try {
        p.setState();
      } catch {
        // point may have been destroyed
      }
    }
    state.prevSyncedPoints = [];

    state.charts.forEach((chart, key) => {
      if (key === sourceKey) {
        return;
      }
      chart.xAxis?.[0]?.hideCrosshair();
      chart.tooltip?.hide();
    });
  }, []);

  /**
   * Returns a stable `onChartRef` callback for a given chart key.
   * The callback is cached per key so repeated calls with the same key
   * return the same function reference (prevents unnecessary re-renders).
   */
  const registerChart = useCallback(
    (key: string) => {
      const state = stateRef.current;
      const cached = state.callbacks.get(key);
      if (cached) {
        return cached;
      }

      const callback = (chart: Chart | null) => {
        // Clean up previous registration for this key
        const prevListeners = stateRef.current.listeners.get(key);
        const prevChart = stateRef.current.charts.get(key);
        if (prevListeners && prevChart?.container) {
          prevListeners.cancelMove();
          prevChart.container.removeEventListener('mousemove', prevListeners.move);
          prevChart.container.removeEventListener('mouseleave', prevListeners.leave);
        }
        stateRef.current.listeners.delete(key);
        stateRef.current.charts.delete(key);

        if (!chart) {
          clearSync(key);
          return;
        }

        stateRef.current.charts.set(key, chart);

        // Throttled handler: find hovered point in source chart, sync to siblings
        const throttledMove = throttle((e: Event) => {
          if (!chart.pointer) {
            return;
          }

          const normalized = chart.pointer.normalize(e as PointerEvent);

          for (const series of chart.series) {
            if (series.visible && series.points?.length) {
              const point = series.searchPoint(normalized, true);
              if (point) {
                const relativeY = chart.plotHeight > 0 ? (normalized.chartY - chart.plotTop) / chart.plotHeight : 0.5;
                syncToX(key, point.x, relativeY);
                return;
              }
            }
          }
        }, 40);

        const onLeave = () => {
          throttledMove.cancel();
          clearSync(key);
        };

        chart.container.addEventListener('mousemove', throttledMove as EventListener);
        chart.container.addEventListener('mouseleave', onLeave);
        stateRef.current.listeners.set(key, {
          move: throttledMove as EventListener,
          leave: onLeave,
          cancelMove: throttledMove.cancel
        });
      };

      stateRef.current.callbacks.set(key, callback);
      return callback;
    },
    [syncToX, clearSync]
  );

  // Cleanup all listeners on unmount
  useEffect(
    () => () => {
      const state = stateRef.current;

      state.listeners.forEach((listeners, key) => {
        listeners.cancelMove();
        const chart = state.charts.get(key);
        if (chart?.container) {
          chart.container.removeEventListener('mousemove', listeners.move);
          chart.container.removeEventListener('mouseleave', listeners.leave);
        }
      });

      state.charts.clear();
      state.listeners.clear();
      state.callbacks.clear();
    },
    []
  );

  return registerChart;
}

export const ChartSyncContext = React.createContext<RegisterChart | null>(null);

interface ChartSyncProviderProps {
  board: { get: (key: string) => unknown };
  children: React.ReactNode;
}

export const ChartSyncProvider = observer(({ board, children }: ChartSyncProviderProps) => {
  const config = board.get('config') as Record<string, unknown> | undefined;
  const syncTooltips = config?.syncTooltips === true;
  const registerChart = useChartSync({ enabled: true, syncTooltips });
  return React.createElement(ChartSyncContext.Provider, { value: registerChart }, children);
});
